import { useMutation } from "@tanstack/react-query";

import {
  caaCustomerAddressCreateMutation,
  caaCustomerAddressDeleteMutation,
  caaCustomerAddressUpdateMutation,
  caaCustomerEmailMarketingSubscribeMutation,
  caaCustomerEmailMarketingUnsubscribeMutation,
  caaCustomerQuery,
  caaCustomerUpdateMutation,
} from "../graphql/customer-account-gql";
import type {
  ICaaCustomerAddressCreateMutation,
  ICaaCustomerAddressDeleteMutation,
  ICaaCustomerAddressUpdateMutation,
  ICaaCustomerEmailMarketingSubscribeMutation,
  ICaaCustomerEmailMarketingUnsubscribeMutation,
  ICaaCustomerQuery,
  ICaaCustomerUpdateMutation,
} from "../interface/customer-account";

/** Customer Account API GraphQL 실행기. 앱이 endpoint discovery/토큰 갱신을 담아 주입한다. */
export type TCaaRequestFn = <TData>(document: string, variables?: Record<string, unknown>) => Promise<TData>;

interface IUseCustomerQueryCaaProps {
  request: TCaaRequestFn;
}

/**
 * Customer Account API customer 쿼리/뮤테이션 바인딩.
 *
 * CAA는 Storefront API와 엔드포인트·인증이 달라 별도 실행기를 쓴다.
 * 전부 no-cache 성격이라 useMutation으로 두고, 호출부에서 명령형으로 실행한다.
 */
export default function useCustomerQueryCaa({ request }: IUseCustomerQueryCaaProps) {
  const fetchCaaCustomerMutation = useMutation({
    mutationFn: () => request<ICaaCustomerQuery>(caaCustomerQuery),
  });

  const updateCaaCustomerMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) => request<ICaaCustomerUpdateMutation>(caaCustomerUpdateMutation, variables),
  });

  const createCaaAddressMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerAddressCreateMutation>(caaCustomerAddressCreateMutation, variables),
  });

  const editCaaAddressMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerAddressUpdateMutation>(caaCustomerAddressUpdateMutation, variables),
  });

  const deleteCaaAddressMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerAddressDeleteMutation>(caaCustomerAddressDeleteMutation, variables),
  });

  const subscribeCaaEmailMarketingMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerEmailMarketingSubscribeMutation>(caaCustomerEmailMarketingSubscribeMutation, variables),
  });

  const unsubscribeCaaEmailMarketingMutation = useMutation({
    mutationFn: (variables: Record<string, unknown>) =>
      request<ICaaCustomerEmailMarketingUnsubscribeMutation>(caaCustomerEmailMarketingUnsubscribeMutation, variables),
  });

  return {
    fetchCaaCustomer: fetchCaaCustomerMutation.mutateAsync,
    fetchCaaCustomerLoading: fetchCaaCustomerMutation.isPending,
    updateCaaCustomer: updateCaaCustomerMutation.mutateAsync,
    updateCaaCustomerLoading: updateCaaCustomerMutation.isPending,
    createCaaAddress: createCaaAddressMutation.mutateAsync,
    createCaaAddressLoading: createCaaAddressMutation.isPending,
    editCaaAddress: editCaaAddressMutation.mutateAsync,
    editCaaAddressLoading: editCaaAddressMutation.isPending,
    deleteCaaAddress: deleteCaaAddressMutation.mutateAsync,
    deleteCaaAddressLoading: deleteCaaAddressMutation.isPending,
    subscribeCaaEmailMarketing: subscribeCaaEmailMarketingMutation.mutateAsync,
    subscribeCaaEmailMarketingLoading: subscribeCaaEmailMarketingMutation.isPending,
    unsubscribeCaaEmailMarketing: unsubscribeCaaEmailMarketingMutation.mutateAsync,
    unsubscribeCaaEmailMarketingLoading: unsubscribeCaaEmailMarketingMutation.isPending,
  };
}
